/**
 * AI Stuck Detection Service
 *
 * Background service that periodically scans for meetings with incomplete AI content
 * and automatically retries generation. This catches failures that might slip through
 * the real-time monitor (e.g., if user navigates away before monitor completes).
 *
 * Runs every 30 seconds and checks:
 * 1. Meetings created in the last 10 minutes
 * 2. Meetings with transcription but missing AI content
 * 3. Attempts automatic recovery
 */

import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";
import { retryWithResilience } from "./ai-generation-resilience";
import { executeWithAuthFallback } from "./auth-utils";
import { getQuotaBlock } from "./ai-quota-guard";
import { isTranscriptLongEnoughForAI } from "./ai-generation-monitor";

export interface StuckMeetingReport {
  meetingId: string;
  hasMissing: string[];
  transcriptionLength: number;
  ageMinutes: number;
  recoveryAttempted: boolean;
  recoverySuccess: boolean;
  /** Server declined the work (already generating / already complete) — no content produced. */
  recoverySkipped: boolean;
  recoveryError?: string;
}

let isServiceRunning = false;
let scanIntervalId: NodeJS.Timeout | null = null;
let cleanupIntervalId: NodeJS.Timeout | null = null;

// Track which meetings we've already warned about (with detailed recovery history)
// to avoid spamming the same warning every 30 seconds
interface StuckMeetingCacheEntry {
  timestamp: number;
  recoveryAttempted: boolean;
  missingFields: string[];
  recoveryAttempts: number;
  recoveryTimestamps: number[]; // Track each recovery attempt time
  lastRecoveryError?: string;
  lastSuccessfulRecovery?: number;
  consecutiveFailures: number; // Count consecutive failed recovery attempts
}

const warnedMeetings = new Map<string, StuckMeetingCacheEntry>();

// Clean up old entries every 5 minutes to prevent memory leaks
const WARN_CACHE_CLEANUP_INTERVAL = 5 * 60 * 1000; // 5 minutes
const WARN_CACHE_TTL = 15 * 60 * 1000; // Keep warnings for 15 minutes
const RECOVERY_COOLDOWN = 45 * 1000; // Don't warn again for 45 seconds (reduced from 60)
const MAX_RECOVERY_ATTEMPTS_BEFORE_SILENCE = 7; // Increased from 5 to 7 before giving up
const MAX_AGE_FOR_RECOVERY_HOURS = 24; // Don't try to recover meetings older than 24 hours
const MAX_MEETINGS_PER_SCAN = 5; // Cap the per-scan blast radius on Anthropic spend
const MAX_RECOVERIES_PER_SCAN = 1; // One recovery per 30s tick; a real backlog drains over minutes, not in one burst

// How long a meeting must sit incomplete before this detector calls it stuck.
//
// "Missing AI fields" is the normal state of a meeting that was just created — the
// DB trigger fires generate-all-insights on transcription_status -> 'completed', and
// that call takes ~30-90s to write summary/action_items/notes. Firing recovery inside
// that window is not a recovery: the server's claim condition lets a caller reclaim a
// row that is *currently* 'processing' whenever insights are still missing, so an early
// retry steals the lock from the in-flight run and pays Anthropic twice for one meeting.
//
// Two windows, because the two states mean different things:
//   - 'processing': a generation is running (or crashed mid-run). Wait out the same
//     10-minute staleness bound the server-side cleanup jobs use before intervening.
//   - anything else: no generation holds the row, so the trigger either finished
//     badly or never fired. Still allow for kickoff latency before declaring it stuck.
const MIN_AGE_BEFORE_STUCK_MS = 2 * 60 * 1000; // 2 min
const MIN_AGE_BEFORE_STUCK_WHILE_PROCESSING_MS = 10 * 60 * 1000; // 10 min

function cleanupWarnedMeetings() {
  const now = Date.now();
  for (const [meetingId, info] of warnedMeetings.entries()) {
    if (now - info.timestamp > WARN_CACHE_TTL) {
      warnedMeetings.delete(meetingId);
    }
  }
}

/**
 * Check if we should warn about a stuck meeting (avoid duplicate warnings)
 * Uses sophisticated logic to detect when to retry vs. when to give up
 */
function shouldWarnAboutMeeting(meetingId: string, missingFields: string[], ageMinutes: number): boolean {
  const cached = warnedMeetings.get(meetingId);

  if (!cached) {
    return true; // First time seeing this stuck meeting, warn
  }

  // If we've already attempted recovery many times and it keeps failing, stop warning
  // (just monitor silently at debug level)
  if (cached.recoveryAttempts >= MAX_RECOVERY_ATTEMPTS_BEFORE_SILENCE) {
    return false;
  }

  // If we just attempted recovery, don't warn again for a bit
  if (cached.recoveryAttempted && Date.now() - cached.timestamp < RECOVERY_COOLDOWN) {
    return false;
  }

  // Check if missing fields changed (different fields = different issue, or new recovery opportunity)
  const fieldsChanged = cached.missingFields.sort().join(',') !== missingFields.sort().join(',');
  if (fieldsChanged) {
    return true; // Missing fields changed, warn again
  }

  // Check if we've had a recent successful recovery (last 5 minutes)
  // If so, wait before warning again (they might still be generating)
  if (cached.lastSuccessfulRecovery && Date.now() - cached.lastSuccessfulRecovery < 5 * 60 * 1000) {
    return false; // Recently recovered, give it time
  }

  // If meeting is very old (>20 hours) and we haven't warned in a while, warn to try again
  if (ageMinutes > 20 * 60 && Date.now() - cached.timestamp > 2 * 60 * 1000) {
    return true; // Very old meeting, warn again for potential recovery
  }

  // Same stuck state as before, don't spam warnings
  return false;
}

/**
 * Start the stuck detection service
 * Should be called once on app initialization
 */
export function startAIStuckDetector(): void {
  if (isServiceRunning) {
    logger.log('[AI-StuckDetector] Service already running');
    return;
  }

  isServiceRunning = true;
  logger.log('[AI-StuckDetector] Service started');

  // Run initial scan after 5 seconds
  setTimeout(() => scanForStuckMeetings(), 5000);

  // Run periodic scans every 30 seconds
  scanIntervalId = setInterval(() => scanForStuckMeetings(), 30 * 1000);

  // Clean up old entries periodically
  cleanupIntervalId = setInterval(cleanupWarnedMeetings, WARN_CACHE_CLEANUP_INTERVAL);
}

/**
 * Stop the stuck detection service
 */
export function stopAIStuckDetector(): void {
  if (scanIntervalId) {
    clearInterval(scanIntervalId);
    scanIntervalId = null;
  }
  if (cleanupIntervalId) {
    clearInterval(cleanupIntervalId);
    cleanupIntervalId = null;
  }
  isServiceRunning = false;
  logger.log('[AI-StuckDetector] Service stopped');
}

/**
 * Scan for meetings with stuck AI generation
 */
async function scanForStuckMeetings(): Promise<StuckMeetingReport[]> {
  try {
    // Every recovery this scan could start would fail on the same exhausted quota,
    // so skip the scan outright rather than rediscovering that once per meeting.
    const quotaBlock = getQuotaBlock();
    if (quotaBlock) {
      logger.debug(
        `[AI-StuckDetector] Skipping scan - Anthropic quota exhausted until ${new Date(quotaBlock.until).toISOString()}`,
      );
      return [];
    }

    // getSession() is cached locally; getUser() would add a network round-trip to
    // every 30s scan. RLS still scopes the query below to this user server-side.
    const { data: { session }, error: userError } = await supabase.auth.getSession();
    const user = session?.user;

    if (userError || !user) {
      // Not authenticated, can't scan
      logger.debug('[AI-StuckDetector] User not authenticated, skipping scan');
      return [];
    }

    // Look for meetings created in the last 24 hours (catch all missed generations)
    // Increased from 10 minutes to 24 hours to catch meetings that were created
    // before the detector started, or where auto-generation was skipped
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

    // Use auth-safe query execution with automatic retry on auth failure
    const { data: meetings, error: queryError, isAuthError } = await executeWithAuthFallback(
      'fetch-stuck-meetings',
      () =>
        supabase
          .from('meetings')
          .select('id, title, transcription, summary, action_items, notes, created_at, updated_at, transcription_status')
          .eq('user_id', user.id)
          .gte('created_at', oneDayAgo)
          .order('created_at', { ascending: false })
          // Each stuck meeting in this page costs real Anthropic spend to recover.
          // Newest-first means a backlog drains a few per scan instead of all at once.
          .limit(MAX_MEETINGS_PER_SCAN)
    );

    if (isAuthError) {
      logger.error('[AI-StuckDetector] Authentication failed, stopping service');
      stopAIStuckDetector();
      return [];
    }

    if (queryError) {
      logger.error('[AI-StuckDetector] Query error:', queryError.message);
      return [];
    }

    if (!meetings || meetings.length === 0) {
      return [];
    }

    const stuckMeetings: StuckMeetingReport[] = [];
    let recoveriesThisScan = 0;

    // Check each meeting for incomplete AI content
    for (const meeting of meetings) {
      if (!isTranscriptLongEnoughForAI(meeting.transcription)) {
        // No/short transcription - the server would reject it, so it isn't stuck
        continue;
      }

      const missingFields: string[] = [];
      if (!meeting.summary || meeting.summary.trim().length < 50) {
        missingFields.push('summary');
      }
      if (!meeting.action_items || meeting.action_items.trim().length < 20) {
        missingFields.push('action_items');
      }
      if (!meeting.notes || meeting.notes.trim().length < 50) {
        missingFields.push('notes');
      }

      if (missingFields.length === 0) {
        // All good, skip this meeting
        continue;
      }

      // Found a stuck meeting, report it
      const ageSeconds = Math.round((Date.now() - new Date(meeting.created_at).getTime()) / 1000);
      const ageMinutes = Math.round(ageSeconds / 60);

      // Don't flag a meeting as stuck while generation could still legitimately be
      // running. Measure from the last write, not from creation: a row the server is
      // actively working on keeps getting touched, and that is the signal that someone
      // else already owns this meeting.
      const isProcessing = meeting.transcription_status === 'processing';
      const lastTouchedAt = meeting.updated_at
        ? new Date(meeting.updated_at).getTime()
        : new Date(meeting.created_at).getTime();
      const idleMs = Date.now() - lastTouchedAt;
      const graceMs = isProcessing
        ? MIN_AGE_BEFORE_STUCK_WHILE_PROCESSING_MS
        : MIN_AGE_BEFORE_STUCK_MS;

      if (idleMs < graceMs) {
        logger.debug(
          `[AI-StuckDetector] Skipping meeting ${meeting.id} (${ageSeconds}s old, ` +
          `status=${meeting.transcription_status ?? 'null'}, idle ${Math.round(idleMs / 1000)}s < ` +
          `${Math.round(graceMs / 1000)}s grace) - generation may still be in flight`
        );
        continue;
      }

      // Only warn if this is a new stuck state or fields changed (avoid spam)
      const shouldWarn = shouldWarnAboutMeeting(meeting.id, missingFields, ageMinutes);
      if (shouldWarn) {
        logger.warn(`[AI-StuckDetector] Found stuck meeting ${meeting.id} (${ageMinutes}min old, missing: ${missingFields.join(', ')})`);
      } else {
        logger.debug(`[AI-StuckDetector] Continuing to monitor stuck meeting ${meeting.id} (${ageMinutes}min old, missing: ${missingFields.join(', ')})`);
      }

      const report: StuckMeetingReport = {
        meetingId: meeting.id,
        hasMissing: missingFields,
        transcriptionLength: meeting.transcription.length,
        ageMinutes,
        recoveryAttempted: false,
        recoverySuccess: false,
        recoverySkipped: false,
      };

      // Attempt recovery
      // More aggressive recovery: auto-recover meetings up to 24 hours old
      // This catches meetings created earlier that missed initial auto-generation
      const maxAgeMinutesForRecovery = MAX_AGE_FOR_RECOVERY_HOURS * 60;
      const cached = warnedMeetings.get(meeting.id);
      const hasExhaustedRetries = cached && cached.consecutiveFailures >= MAX_RECOVERY_ATTEMPTS_BEFORE_SILENCE;
      const recoveryBudgetLeft = recoveriesThisScan < MAX_RECOVERIES_PER_SCAN;

      if (ageMinutes < maxAgeMinutesForRecovery && !hasExhaustedRetries && recoveryBudgetLeft) {
        recoveriesThisScan++;
        if (shouldWarn) {
          logger.log(`[AI-StuckDetector] Attempting automatic recovery for meeting ${meeting.id} (${ageMinutes}min old, missing: ${missingFields.join(', ')})`);
        } else {
          logger.debug(`[AI-StuckDetector] Retrying recovery for meeting ${meeting.id} (attempt ${(cached?.recoveryAttempts || 0) + 1})`);
        }
        report.recoveryAttempted = true;

        const result = await retryWithResilience(
          meeting.id,
          meeting.transcription,
          undefined, // template
          2 // background service: the 30s scan already retries, so a deep ladder here just multiplies spend
        );

        report.recoverySkipped = result.success && result.skipped === true;
        report.recoverySuccess = result.success && !report.recoverySkipped;

        if (report.recoverySkipped) {
          // The server declined — someone else owns this meeting right now, or it is
          // already complete and our snapshot was stale. Neither is a failure worth
          // counting against the retry budget, and neither is a recovery: booking it
          // as one would reset consecutiveFailures and mute warnings for 5 minutes on
          // a meeting that still has no content. Just hold the cooldown and re-check.
          logger.debug(`[AI-StuckDetector] Recovery skipped by server for ${meeting.id} - re-checking next scan`);

          const previousInfo = warnedMeetings.get(meeting.id);
          warnedMeetings.set(meeting.id, {
            timestamp: Date.now(),
            recoveryAttempted: true,
            missingFields,
            recoveryAttempts: previousInfo?.recoveryAttempts || 0,
            recoveryTimestamps: previousInfo?.recoveryTimestamps || [],
            lastRecoveryError: previousInfo?.lastRecoveryError,
            lastSuccessfulRecovery: previousInfo?.lastSuccessfulRecovery,
            consecutiveFailures: previousInfo?.consecutiveFailures || 0,
          });
        } else if (!result.success) {
          report.recoveryError = result.error;

          // If this is an auth error, stop the service
          if (result.isAuthError) {
            logger.debug(`[AI-StuckDetector] Authentication error during recovery, stopping service`);
            stopAIStuckDetector();
            return [];
          }

          if (shouldWarn) {
            logger.error(`[AI-StuckDetector] Recovery failed for ${meeting.id}: ${result.error}`);
          }

          // Track consecutive failures
          const previousInfo = warnedMeetings.get(meeting.id);
          const consecutiveFailures = previousInfo ? (previousInfo.consecutiveFailures + 1) : 1;

          if (consecutiveFailures >= MAX_RECOVERY_ATTEMPTS_BEFORE_SILENCE) {
            logger.error(`[AI-StuckDetector] Meeting ${meeting.id} failed ${consecutiveFailures}x in a row, giving up`);
          }

          warnedMeetings.set(meeting.id, {
            timestamp: Date.now(),
            recoveryAttempted: true,
            missingFields,
            recoveryAttempts: (previousInfo?.recoveryAttempts || 0) + 1,
            recoveryTimestamps: [...(previousInfo?.recoveryTimestamps || []), Date.now()],
            lastRecoveryError: result.error,
            lastSuccessfulRecovery: previousInfo?.lastSuccessfulRecovery,
            consecutiveFailures,
          });
        } else {
          logger.log(`[AI-StuckDetector] Recovery succeeded for meeting ${meeting.id}`);

          // Track successful recovery and reset consecutive failures
          const previousInfo = warnedMeetings.get(meeting.id);
          warnedMeetings.set(meeting.id, {
            timestamp: Date.now(),
            recoveryAttempted: true,
            missingFields: [],
            recoveryAttempts: (previousInfo?.recoveryAttempts || 0) + 1,
            recoveryTimestamps: [...(previousInfo?.recoveryTimestamps || []), Date.now()],
            lastSuccessfulRecovery: Date.now(),
            consecutiveFailures: 0,
          });
        }
      } else if (hasExhaustedRetries) {
        logger.debug(`[AI-StuckDetector] Meeting ${meeting.id} exhausted recovery attempts (${cached?.consecutiveFailures} consecutive failures), monitoring silently`);
      } else {
        logger.debug(`[AI-StuckDetector] Meeting ${meeting.id} too old (${ageMinutes}min > ${maxAgeMinutesForRecovery}min), skipping auto-recovery`);
      }

      stuckMeetings.push(report);
    }

    if (stuckMeetings.length > 0) {
      const recovered = stuckMeetings.filter(r => r.recoverySuccess).length;
      const skipped = stuckMeetings.filter(r => r.recoverySkipped).length;
      logger.warn(
        `[AI-StuckDetector] Found ${stuckMeetings.length} stuck meetings, ${recovered} recovered` +
        (skipped > 0 ? `, ${skipped} skipped (already in progress)` : '')
      );
    }

    return stuckMeetings;

  } catch (error) {
    logger.error('[AI-StuckDetector] Scan error:', error);
    return [];
  }
}

/**
 * Manually trigger a scan (for debugging)
 */
export async function manualScan(): Promise<StuckMeetingReport[]> {
  logger.log('[AI-StuckDetector] Manual scan triggered');
  return scanForStuckMeetings();
}

/**
 * Manually recover a specific meeting (for debugging/manual fixing)
 */
export async function manualRecoverMeeting(
  meetingId: string,
  transcription: string
): Promise<{ success: boolean; error?: string }> {
  logger.log(`[AI-StuckDetector] Manual recovery triggered for meeting ${meetingId}`);

  try {
    const result = await retryWithResilience(
      meetingId,
      transcription,
      undefined,
      5 // max 5 retries for manual recovery
    );

    if (result.success) {
      logger.log(`[AI-StuckDetector] Manual recovery succeeded for meeting ${meetingId}`);
    } else {
      logger.error(`[AI-StuckDetector] Manual recovery failed for meeting ${meetingId}: ${result.error}`);
    }

    return result;
  } catch (error) {
    const errorMsg = String(error);
    logger.error(`[AI-StuckDetector] Manual recovery exception for meeting ${meetingId}:`, error);
    return { success: false, error: errorMsg };
  }
}

/**
 * Get detailed recovery history for a meeting
 */
export function getRecoveryHistory(meetingId: string): StuckMeetingCacheEntry | null {
  return warnedMeetings.get(meetingId) || null;
}

/**
 * Get all stuck meetings being monitored
 */
export function getMonitoredStuckMeetings(): Array<{ meetingId: string; info: StuckMeetingCacheEntry }> {
  return Array.from(warnedMeetings.entries()).map(([meetingId, info]) => ({
    meetingId,
    info,
  }));
}

/**
 * Get service status
 */
export function getDetectorStatus(): {
  running: boolean
  description: string
  monitoredMeetings: number
  configuration: {
    scanIntervalMs: number
    maxAgeHours: number
    maxRecoveryAttempts: number
  }
} {
  return {
    running: isServiceRunning,
    description: isServiceRunning
      ? 'AI stuck detector is running and scanning every 30 seconds for incomplete AI content'
      : 'AI stuck detector is not running',
    monitoredMeetings: warnedMeetings.size,
    configuration: {
      scanIntervalMs: 30 * 1000,
      maxAgeHours: MAX_AGE_FOR_RECOVERY_HOURS,
      maxRecoveryAttempts: MAX_RECOVERY_ATTEMPTS_BEFORE_SILENCE,
    },
  };
}
